/**
 * Prediction Utility Functions
 * 
 * Provides helper functions for working with predictions, week types, and prediction formatting.
 */

import { ELIMINATION_TYPES } from '../models/dataModels';

/**
 * Determine elimination style for a normal (non-finale) week
 * @param {Object} week - Week object with eliminationType field
 * @returns {string} - 'single' | 'double' | 'double_simultaneous' | 'triple'
 */
export function getWeekEliminationType(week) {
  if (!week) return 'single';

  if (week.eliminationType === ELIMINATION_TYPES.TRIPLE) return 'triple';
  if (week.eliminationType === ELIMINATION_TYPES.DOUBLE_SIMULTANEOUS) {
    return 'double_simultaneous';
  }
  if (week.eliminationType === ELIMINATION_TYPES.DOUBLE) return 'double';
  if (week.eliminationType === ELIMINATION_TYPES.SINGLE) return 'single';

  if (week.isFinale) return 'single';

  return 'single';
}

/**
 * Double or double simultaneous (two ordered picks) or triple
 * @param {Object} week
 * @returns {boolean}
 */
export function isMultiEliminationWeek(week) {
  const t = week?.eliminationType;
  return (
    t === ELIMINATION_TYPES.DOUBLE ||
    t === ELIMINATION_TYPES.DOUBLE_SIMULTANEOUS ||
    t === ELIMINATION_TYPES.TRIPLE
  );
}

/**
 * Standard double or simultaneous double (two boot slots, not triple)
 * @param {Object} week
 * @returns {boolean}
 */
export function isDoubleLikeEliminationWeek(week) {
  const t = week?.eliminationType;
  return t === ELIMINATION_TYPES.DOUBLE || t === ELIMINATION_TYPES.DOUBLE_SIMULTANEOUS;
}

/**
 * Simultaneous double: same UI/results as double; survival scored as one episode
 * @param {Object} week
 * @returns {boolean}
 */
export function isSimultaneousDoubleEliminationWeek(week) {
  return week?.eliminationType === ELIMINATION_TYPES.DOUBLE_SIMULTANEOUS;
}

/**
 * Human-readable elimination label for a week (badges, summaries)
 * @param {Object} week
 * @returns {string}
 */
export function getEliminationTypeDisplayLabel(week) {
  const t = week?.eliminationType;
  if (t === ELIMINATION_TYPES.TRIPLE) return 'Triple Elimination';
  if (t === ELIMINATION_TYPES.DOUBLE_SIMULTANEOUS) {
    return 'Double Elimination (Simultaneous)';
  }
  if (t === ELIMINATION_TYPES.DOUBLE) return 'Double Elimination';
  if (t === ELIMINATION_TYPES.SINGLE) return 'Single Elimination';
  return '';
}

/**
 * Week results use ordered tribals: [{ contestantName|contestantId, position }, ...]
 * @param {Object} results - week.results
 * @returns {boolean}
 */
export function isOrderedVotedOutResults(results) {
  return (
    Array.isArray(results?.votedOut) &&
    results.votedOut.length > 0 &&
    results.votedOut[0]?.position != null
  );
}

/**
 * Check if a week is open for predictions
 * @param {Object} week - Week object
 * @returns {boolean} - True if week is open for predictions
 */
export function isWeekOpenForPredictions(week) {
  if (!week) return false;
  return week.isOpenForPredictions === true;
}

/**
 * Check if a week has results entered
 * @param {Object} week - Week object
 * @returns {boolean} - True if week has results
 */
export function hasWeekResults(week) {
  if (!week || !week.results) return false;
  
  // Check if any result field is populated
  return !!(
    week.results.votedOut ||
    week.results.winner ||
    week.results.fireMakingLoser ||
    week.results.immunityWinner ||
    week.results.rewardWinner
  );
}

/**
 * Get the number of voted-out predictions required for a normal week
 * @param {Object} week - Week object
 * @returns {number} - 1, 2, or 3 (finale uses other fields)
 */
export function getRequiredPredictionCount(week) {
  const eliminationType = getWeekEliminationType(week);
  if (eliminationType === 'triple') return 3;
  if (eliminationType === 'double' || eliminationType === 'double_simultaneous') {
    return 2;
  }
  return 1;
}

/**
 * Format prediction for display
 * Converts contestant IDs to names for display
 * @param {Object} prediction - Prediction object with contestant IDs
 * @param {Array} contestants - Array of contestant objects with id and name
 * @param {Function} normalizeToContestantName - Function to normalize contestant ID to name
 * @returns {Object} - Formatted prediction with names
 */
export function formatPredictionForDisplay(prediction, contestants, normalizeToContestantName) {
  if (!prediction || !normalizeToContestantName) return null;
  
  const formatted = {
    ...prediction,
    predictions: {}
  };
  
  if (prediction.predictions) {
    // Handle votedOut (can be array or single value)
    if (prediction.predictions.votedOut) {
      if (Array.isArray(prediction.predictions.votedOut)) {
        formatted.predictions.votedOut = prediction.predictions.votedOut.map(
          id => normalizeToContestantName(id, contestants)
        );
      } else {
        formatted.predictions.votedOut = normalizeToContestantName(
          prediction.predictions.votedOut,
          contestants
        );
      }
    }
    
    // Handle other prediction fields (single values)
    if (prediction.predictions.winner) {
      formatted.predictions.winner = normalizeToContestantName(
        prediction.predictions.winner,
        contestants
      );
    }
    
    if (prediction.predictions.fireMakingLoser) {
      formatted.predictions.fireMakingLoser = normalizeToContestantName(
        prediction.predictions.fireMakingLoser,
        contestants
      );
    }
    
    if (prediction.predictions.immunityWinner) {
      formatted.predictions.immunityWinner = normalizeToContestantName(
        prediction.predictions.immunityWinner,
        contestants
      );
    }
    
    if (prediction.predictions.rewardWinner) {
      formatted.predictions.rewardWinner = normalizeToContestantName(
        prediction.predictions.rewardWinner,
        contestants
      );
    }
  }
  
  return formatted;
}

/**
 * Normalize prediction for storage
 * Converts contestant names to IDs for storage
 * @param {Object} prediction - Prediction object with contestant names or IDs
 * @param {Array} contestants - Array of contestant objects with id and name
 * @param {Function} normalizeToContestantId - Function to normalize contestant name to ID
 * @returns {Object} - Normalized prediction with IDs
 */
export function normalizePredictionForStorage(prediction, contestants, normalizeToContestantId) {
  if (!prediction || !prediction.predictions || !normalizeToContestantId) return prediction;
  
  const normalized = {
    ...prediction,
    predictions: {}
  };
  
  // Handle votedOut (can be array or single value)
  if (prediction.predictions.votedOut) {
    if (Array.isArray(prediction.predictions.votedOut)) {
      normalized.predictions.votedOut = prediction.predictions.votedOut
        .map(nameOrId => normalizeToContestantId(nameOrId, contestants))
        .filter(Boolean);
    } else {
      const id = normalizeToContestantId(prediction.predictions.votedOut, contestants);
      if (id) {
        normalized.predictions.votedOut = id;
      }
    }
  }
  
  // Handle other prediction fields (single values)
  if (prediction.predictions.winner) {
    const id = normalizeToContestantId(prediction.predictions.winner, contestants);
    if (id) normalized.predictions.winner = id;
  }
  
  if (prediction.predictions.fireMakingLoser) {
    const id = normalizeToContestantId(prediction.predictions.fireMakingLoser, contestants);
    if (id) normalized.predictions.fireMakingLoser = id;
  }
  
  if (prediction.predictions.immunityWinner) {
    const id = normalizeToContestantId(prediction.predictions.immunityWinner, contestants);
    if (id) normalized.predictions.immunityWinner = id;
  }
  
  if (prediction.predictions.rewardWinner) {
    const id = normalizeToContestantId(prediction.predictions.rewardWinner, contestants);
    if (id) normalized.predictions.rewardWinner = id;
  }
  
  return normalized;
}

/**
 * Check if a prediction is complete for a given week
 * @param {Object} prediction - Prediction object
 * @param {Object} week - Week object
 * @returns {boolean} - True if prediction has all required fields
 */
export function isPredictionComplete(prediction, week) {
  if (!prediction || !prediction.predictions || !week) return false;
  
  const requiredCount = getRequiredPredictionCount(week);
  
  // Check votedOut predictions
  if (week.isFinale) {
    // Finale requires winner and possibly fireMakingLoser
    return !!(
      prediction.predictions.winner &&
      (!week.results?.fireMakingLoser || prediction.predictions.fireMakingLoser)
    );
  } else {
    // Regular week requires votedOut
    if (requiredCount >= 2) {
      return (
        Array.isArray(prediction.predictions.votedOut) &&
        prediction.predictions.votedOut.length === requiredCount
      );
    }
    return !!prediction.predictions.votedOut;
  }
}

